import React, { useCallback, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import classnames from "classnames";
import { Button } from "@shared/components";
import {
  SensorDataFile,
  SensorDataSession,
  SensorDataSessionMarker,
  SensorDataSessionMarkerDot,
  Station,
} from "@shared/models";
import { actions as sharedActions } from "@shared/store";
import { MODAL_TYPES } from "@shared/constants";
import { SensorDataSessionMarkerFormShape } from "@containers/SensorDataSession/interfaces";
import { useContainerDimensions } from "@shared/hooks";
import { excelDownlod, getFormattedDate } from "@shared/utils";

import { SensorDataSessionMarkers, SensorDataSessionMarkerDotsTable } from "./components";
import { prepareExcelData, RIGHT_MARKERS_WIDTH } from "./helpers";

import "./index.scss";

interface SensorDataSessionChartDotsListProps {
  session?: SensorDataSession | null;
  files: SensorDataFile[];
  stations: Partial<Station>[];
  markers: SensorDataSessionMarker[];
  dots: SensorDataSessionMarkerDot[];
  activeMarker: SensorDataSessionMarker | null;
  setActiveMarker: (marker: SensorDataSessionMarker | null) => void;
  onCreateMarker: (values: SensorDataSessionMarkerFormShape) => void;
  onDeleteMarker: (marker: SensorDataSessionMarker) => void;
  onDeleteDot: (dot: SensorDataSessionMarkerDot) => void;
  onRowClick: (dot: SensorDataSessionMarkerDot) => void;
}

const SensorDataSessionChartDotsList = (props: SensorDataSessionChartDotsListProps) => {
  const {
    session,
    markers,
    onCreateMarker,
    activeMarker,
    setActiveMarker,
    dots,
    onDeleteDot,
    files,
    onRowClick,
    onDeleteMarker,
    stations,
  } = props;

  const dispatch = useDispatch();

  const markersRef = useRef<HTMLDivElement>(null);
  const markersDimensionsInfo = useContainerDimensions(markersRef);

  const currentSessionDotsByActiveMarker = useMemo(() => {
    if (!activeMarker) return [];

    return dots.filter((dot) => dot.session_marker_id === activeMarker.id);
  }, [activeMarker, dots]);

  const createMarker = useCallback(() => {
    if (!files.length && !stations.length) return;
    dispatch(
      sharedActions.showModal({
        type: MODAL_TYPES.CREATE_MARKER_FORM,
        props: {
          onSubmit: (values: SensorDataSessionMarkerFormShape) => {
            onCreateMarker(values);
            dispatch(sharedActions.hideModal());
          },
        },
      }),
    );
  }, [dispatch, onCreateMarker, files, stations]);

  const handleDeleteDot = useCallback(
    (dot: SensorDataSessionMarkerDot) => {
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Delete",
            content: `Are you sure you want to delete dot?`,
            cancelBtnText: "Cancel",
            successBtnText: "Delete",
            onSuccess: () => {
              onDeleteDot(dot);
              dispatch(sharedActions.hideModal());
            },
          },
        }),
      );
    },
    [dispatch, onDeleteDot],
  );

  const handleDeleteMarker = useCallback(
    (marker: SensorDataSessionMarker) => {
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Delete marker",
            content:
              "Are you sure you want to delete the marker? If there're any dots connected to that marker, they will be deleted as well.",
            cancelBtnText: "Cancel",
            successBtnText: "Delete",
            onSuccess: () => {
              onDeleteMarker(marker);
              dispatch(sharedActions.hideModal());
            },
          },
        }),
      );
    },
    [dispatch, onDeleteMarker],
  );

  const handleDownloadExcel = useCallback(() => {
    if (session) {
      const excelData = prepareExcelData(session, markers, dots, files, stations);
      excelDownlod(excelData, `${session.name} - ${getFormattedDate(new Date())}`);
    }
  }, [markers, dots, session, files, stations]);

  useEffect(() => {
    setActiveMarker(markers.find(Boolean) || null);
  }, [markers, setActiveMarker]);

  return (
    <div className="sensor-data-session-chart-dots">
      <div className="markers" ref={markersRef}>
        <div className="markers-left">
          {markers.length && (files.length || stations.length) ? (
            <div className="marker-items">
              <SensorDataSessionMarkers
                width={markersDimensionsInfo.width - RIGHT_MARKERS_WIDTH}
                markers={markers}
                activeMarker={activeMarker}
                onSelectMarker={setActiveMarker}
                onDeleteMarker={handleDeleteMarker}
              />
            </div>
          ) : null}
          <div className={classnames("marker-add-new", { disabled: !files.length && !stations.length })}>
            <span onClick={() => createMarker()}>+ Add New Marker</span>
          </div>
        </div>
        <div className="markers-right">
          <Button
            className="export-marker-btn full-width"
            onClick={() => handleDownloadExcel()}
            disabled={!markers.length}
          >
            Export
          </Button>
        </div>
      </div>
      <div className="marker-dots">
        {currentSessionDotsByActiveMarker.length ? (
          <div className="dots-table">
            <SensorDataSessionMarkerDotsTable
              dots={currentSessionDotsByActiveMarker}
              onDeleteDot={handleDeleteDot}
              onRowClick={onRowClick}
              files={files}
              stations={stations}
            />
          </div>
        ) : (
          <div className="empty-dots-box">
            <div className="empty-dots-title">{markers.length ? "No dots to display" : "No Markers to display"}</div>
            <div className="empty-dots-text">
              {markers.length ? "Mark a dot on the Graph to see data here" : "Please add new marker"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SensorDataSessionChartDotsList;
